import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useCallback} from 'react';
import {useColorScheme} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'extensions/navigation';
import {useModal} from 'extensions/viewport/useModal';
import {Choice} from 'interface/base/Choice';
import {selectors} from 'store/slices/files';
import {Store} from 'store/slices/files/types';
import {vectors, colors} from 'config/styles';

import * as effects from 'interface/hooks/effects/files';

export function useModalFilter(folderKey: string) {
  const {t} = useLingui();
  const scheme = useColorScheme();
  const modal = useModal();
  const sort = useSelector(selectors.getSort);
  const filter = useSelector(selectors.getFilter);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const select = useCallback((type: Store['filter']['type']) => {
    effects.filter(navigate, dispatch, folderKey)(sort, {type});
    modal.exit();
  }, [folderKey, navigate, dispatch]);

  const icon = useCallback((target?: string) => 
    <Svg viewBox="0 0 24 24" width={16} height={16}>
      <Path
        d={filter.type === target
          ? vectors.icons.radioOn
          : vectors.icons.radioOff}
        fill={target
          ? filter.type === target
            ? scheme === 'dark'
              ? colors.neutral.white
              : colors.neutral.darkest
            : colors.neutral.mid
          : 'transparent'}
      />
    </Svg>
  , [filter]);

  const open = useCallback(() => {
    modal.open(
      <Choice
        title={t`Filter Items`}
        buttons={[
          {
            text: t`All Items`,
            icon: icon('all'),
            onPress: () => select('all'),
          },
          {
            text: t`Public`,
            icon: icon('public'),
            onPress: () => select('public'),
          },
          {
            text: t`Private`,
            icon: icon('private'),
            onPress: () => select('private'),
          },
          {
            text: t`Images`,
            icon: icon('images'),
            onPress: () => select('images'),
          },
          {
            text: t`Video`,
            icon: icon('video'),
            onPress: () => select('video'),
          },
          {
            text: t`Audio`,
            icon: icon('audio'),
            onPress: () => select('audio'),
          },
          {
            text: t`Documents`,
            icon: icon('document'),
            onPress: () => select('document'),
          },
          {
            text: t`Spreadsheets`,
            icon: icon('spreadsheet'),
            onPress: () => select('spreadsheet'),
          },
          {
            text: t`Presentation`,
            icon: icon('presentation'),
            onPress: () => select('presentation'),
          },
          {
            text: t`Development`,
            icon: icon('development'),
            onPress: () => select('development'),
          },
          {
            text: t`Cancel`,
            icon: icon(),
            cancel: true,
            onPress: modal.exit,
          },
        ]}
      />
    );
  }, [filter]);

  return {open, exit: modal.exit};
}
